// react
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';

// material ui
import { Container, Grid, Stack } from '@mui/material';

// api
import { useGatewaysGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/gateways';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import {
  useDeleteBillingMeterOnBuildingMutation,
  useGetBuildingByIdQuery,
  usePostBillingMeterOnBuildingMutation,
  usePostSubscriptionOnBuildingMutation,
} from '@/api/recalc/endpoints/buildingApiSlice';
import { useGetJobSpecificationsQuery } from '@/api/recalc/endpoints/jobSpecificationApiSlice';
import {
  useDeleteJobSubscriptionMutation,
  useGetSubscriptionsByBuildingIdQuery,
  usePostSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import {
  useGetAlgorithmInputFormSpecificationByJobTypeQuery,
  useGetAlgorithmInputSpecificationByJobTypeQuery,
  useGetOccupancyPredictionAnalysisQuery,
  useLazyGetOccupancyPredictionAnalysisQuery,
} from '@/api/subscription-manager/algorithmInputSlice';
import {
  useGetActuatorPlanQuery,
  useUpdateMachineLearningModelMutation,
} from '@/api/subscription-manager/predictiveHeatControlSlice';

// shared components
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import AlgorithmInputAdvancedDialog from '@/features/building-optimization/components/algorithm-input/AlgorithmInputAdvancedDialog';
import AlgorithmInputSimpleDialog from '@/features/building-optimization/components/algorithm-input/AlgorithmInputSimpleDialog';
import EditBuildingBillingMeterDialog from '@/features/building-optimization/components/building/EditBuildingBillingMeterDialog';
import OccupancyPredictionForm from '@/features/building-optimization/components/OccupancyPredictionForm';
import DeleteSubscriptionDialog from '@/features/building-optimization/components/subscription/DeleteSubscriptionDialog';
import JobSubscriptionCreate from '@/features/building-optimization/components/subscription/JobSubscriptionCreate';
import JobSubscriptionEdit from '@/features/building-optimization/components/subscription/JobSubscriptionEdit';
import ResetSubscriptionDialog from '@/features/building-optimization/components/subscription/ResetSubscriptionDialog';
import SubscriptionDataGrid from '@/features/building-optimization/components/subscription/SubscriptionDataGrid';

// components
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';

import { DateTime } from 'luxon';

function extractNumberFromURL(url) {
  const matches = url?.match(/(\d+)$/);

  if (matches) {
    return parseInt(matches[1], 10);
  } else {
    return null;
  }
}

function formatAndEncodeTimestampForAPI(date) {
  // Convert the date to ISO format with the local timezone offset
  const isoString = DateTime.fromJSDate(date).toISO(); // Uses the local time zone by default
  return encodeURIComponent(isoString); // Encode for URL
}

export const Route = createFileRoute(
  '/$accountId/_admin/operations/predictive-heat-control/buildings/$buildingName',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('buildingOptimization');
  const account = useSelector(getSelectedAccount);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBuilding = location.state?.building;
  const accounts = location.state?.accounts;

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const jobType = selectedSubscription?.JobType;

  const { data: algorithmInputSpec } =
    useGetAlgorithmInputSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const { data: algorithmInputFormSpec } =
    useGetAlgorithmInputFormSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const [openCreateSubscription, setOpenCreateSubscription] = useState(false);
  const [openEditSubscription, setOpenEditSubscription] = useState(false);
  const [openDeleteSubscription, setOpenDeleteSubscription] = useState(false);
  const [openResetModel, setOpenResetModel] = useState(false);
  const [algorithmInputOpen, setAlgorithmInputOpen] = useState(false);
  const [algorithmInputFormOpen, setAlgorithmInputFormOpen] = useState(false);

  const [modelAnalysisDialogOpen, setModelAnalysisDialogOpen] = useState(false);
  const [generatedOccupancyPlan, setGeneratedOccupancyPlan] = useState(null);
  const [isGeneratingOccupancyAnalysis, setIsGeneratingOccupancyAnalysis] =
    useState(false);

  const [
    openEditBuildingBillingMetersDialog,
    setOpenEditBuildingBillingMetersDialog,
  ] = useState(false);

  const [updateMachineLearningModel] = useUpdateMachineLearningModelMutation();

  const { data: subscriptions, refetch: refetchSubscriptions } =
    useGetSubscriptionsByBuildingIdQuery(selectedBuilding?.BuildingId, {
      skip: !selectedBuilding?.BuildingId,
      // pollingInterval: 10000,
    });

  const { data: building } = useGetBuildingByIdQuery(
    selectedBuilding?.BuildingId,
    {
      skip: !selectedBuilding?.BuildingId,
    },
  );

  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${selectedBuilding?.AccountId})` },
    {
      skip: !selectedBuilding?.AccountId,
    },
  );

  const { data: gateways } = useGatewaysGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${selectedBuilding?.AccountId})` },
    {
      skip: !selectedBuilding?.AccountId,
    },
  );

  const { data: jobSpecifications } = useGetJobSpecificationsQuery();

  const { data: publishedActuatorPlan } = useGetActuatorPlanQuery(
    { id: selectedSubscription?.JobSubscriptionId },
    {
      skip:
        !selectedSubscription?.JobSubscriptionId ||
        !selectedSubscription.JobType ||
        selectedSubscription.JobType !== 'PredictiveHeatControl',
    },
  );

  const [fetchOccupancyPredictionAnalysis] =
    useLazyGetOccupancyPredictionAnalysisQuery();

  const occupancySourceExclusions =
    selectedSubscription?.AlgorithmInput?.predictive_occupancy_detection
      ?.general?.occupancy_source_exclusion || [];

  const { data: occupancyPrediction } = useGetOccupancyPredictionAnalysisQuery(
    {
      jobSubscriptionId: selectedSubscription?.JobSubscriptionId,
      start: formatAndEncodeTimestampForAPI(DateTime.now().toUTC().toMillis()),
      end: formatAndEncodeTimestampForAPI(
        DateTime.now().plus({ days: 7 }).toUTC().toMillis(),
      ),
      exclusions: occupancySourceExclusions,
    },
    { skip: !selectedSubscription?.JobSubscriptionId },
  );

  const [postSubscription] = usePostSubscriptionMutation();
  const [deleteSubscription] = useDeleteJobSubscriptionMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [postSubscriptionOnBuilding] = usePostSubscriptionOnBuildingMutation();
  const [postBillingMeter] = usePostBillingMeterOnBuildingMutation();
  const [deleteBillingMeter] = useDeleteBillingMeterOnBuildingMutation();

  const handleViewSubscription = (subscription) => {
    navigate({
      to: `/$accountId/operations/predictive-heat-control/subscriptions/${encodeURIComponent(subscription.DisplayName)}`,

      state: {
        subscription: subscription,
        building: building,
        from: location, // store current location
      },
    });
  };

  const handleAddBillingMeter = async (billingMeter) => {
    const result = await postBillingMeter({
      buildingId: building.BuildingId,
      billingMeter,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Billing meter was added',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleDeleteBillingMeter = async (billingMeterId) => {
    const result = await deleteBillingMeter({
      buildingId: building.BuildingId,
      billingMeterId,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Billing meter was removed',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleCreateSubscription = async (subscription) => {
    const result = await postSubscription(subscription);

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t?.('subscription.created') || 'Subscription was created',
          severity: 'success',
          response: result.data,
        }),
      );
      const subscriptionId = extractNumberFromURL(result.data.location);

      if (subscriptionId && building) {
        const buildingResult = await postSubscriptionOnBuilding({
          buildingId: building.BuildingId,
          subscriptionId,
        });

        if (!('error' in buildingResult)) {
          dispatch(
            showSnackbar({
              message:
                t?.('subscription.addedToBuilding', {
                  buildingName: building.Name,
                }) || `Subscription was added to ${building.Name}`,
              severity: 'success',
              response: buildingResult.data,
            }),
          );
        }
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
      }
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleUpdateSubscription = async (subscription) => {
    const result = await updateSubscription({
      id: selectedSubscription.JobSubscriptionId,
      patch: subscription,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Subscription was updated',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  // * Move to DeleteSubscriptionDialog
  const handleDeleteSubscriptionConfirmation = async () => {
    const result = await deleteSubscription(
      selectedSubscription.JobSubscriptionId,
    );

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Subscription was deleted',
          severity: 'success',
          response: result.data,
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    setOpenDeleteSubscription(false);
  };

  const handleResetMachineLearningModel = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenResetModel(true);
  };

  const handleResetMachineLearningModelConfirmation = async (body) => {
    const result = await updateMachineLearningModel({
      id: selectedSubscription.JobSubscriptionId,
      data: body,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: 'Machine learning model reset successfully',
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    setOpenResetModel(false);
  };

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenEditSubscription(true);
  };

  const handleDeleteSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenDeleteSubscription(true);
  };

  const handleAlgorithmInputOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputOpen(true);
  };

  const handleAlgorithmInputFormOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputFormOpen(true);
  };

  const handleOccupancyPredictionAnalysisOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setModelAnalysisDialogOpen(true);
  };

  const generateOccupancyPredictionAnalysis = async (formData) => {
    setIsGeneratingOccupancyAnalysis(true); // Start loading

    try {
      const result = await fetchOccupancyPredictionAnalysis({
        jobSubscriptionId: selectedSubscription.JobSubscriptionId,
        duration: formData.duration,
        filterErraticPredictions: formData.filterErraticPredictions,
        exclusions: formData.exclusions,
      });

      // Log the raw result before unwrapping or processing it.

      if (result.data) {
        setGeneratedOccupancyPlan(result.data.occupancy_plan);
      } else {
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
        console.warn('No data in response');
      }
    } catch (error) {
      console.error('Error fetching occupancy prediction analysis:', error);
      setGeneratedOccupancyPlan(null);
    } finally {
      setIsGeneratingOccupancyAnalysis(false); // Stop loading
    }
  };

  useBreadcrumb(building?.Name || null);

  if (!building) return null;

  console.log('Building Detail Rendered with Building:', building);

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <PageHeader title={building?.Name || 'Building Detail'} />
      <Stack gap={2}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <SubscriptionDataGrid
              subscriptions={
                subscriptions?.filter(
                  (s) => s.JobType === 'PredictiveHeatControl',
                ) || []
              }
              accounts={accounts}
              onSubscriptionCreate={() => setOpenCreateSubscription(true)}
              onSubscriptionDelete={handleDeleteSubscriptionConfirmation}
              handleAlgorithmInputFormOpen={handleAlgorithmInputFormOpen}
              handleAlgorithmInputOpen={handleAlgorithmInputOpen}
              handleOccupancyPredictionAnalysisOpen={
                handleOccupancyPredictionAnalysisOpen
              }
              handleViewSubscription={handleViewSubscription}
              handleEditSubscription={handleEditSubscription}
              handleDeleteSubscription={handleDeleteSubscription}
              handleResetMachineLearningModel={handleResetMachineLearningModel}
              refetchSubscriptions={refetchSubscriptions}
            />
          </Grid>
        </Grid>
      </Stack>

      <JobSubscriptionCreate
        open={openCreateSubscription}
        onClose={() => setOpenCreateSubscription(false)}
        selectedAccount={account}
        location={selectedBuilding?.location}
        selectedBuilding={building}
        units={units}
        jobSpecifications={jobSpecifications}
        submit={handleCreateSubscription}
      />

      <JobSubscriptionEdit
        key={selectedSubscription?.JobSubscriptionId ?? 'edit'}
        open={openEditSubscription}
        onClose={() => setOpenEditSubscription(false)}
        submit={handleUpdateSubscription}
        subscription={selectedSubscription}
        location={selectedBuilding?.location}
        jobSpecifications={jobSpecifications}
        units={units}
        gateways={gateways}
      />

      <EditBuildingBillingMeterDialog
        key={selectedSubscription?.JobSubscriptionId ?? 'edit-billing-meters'}
        open={openEditBuildingBillingMetersDialog}
        onClose={() => setOpenEditBuildingBillingMetersDialog(false)}
        options={units}
        billingMeters={building?.BillingMeters || []}
        handleAddBillingMeter={handleAddBillingMeter}
        handleDeleteBillingMeter={handleDeleteBillingMeter}
      />

      <DeleteSubscriptionDialog
        key={selectedSubscription?.JobSubscriptionId ?? 'delete'}
        open={openDeleteSubscription}
        onClose={() => setOpenDeleteSubscription(false)}
        subscription={selectedSubscription}
        handleDeleteSubscription={handleDeleteSubscriptionConfirmation}
      />

      <ResetSubscriptionDialog
        key={selectedSubscription?.JobSubscriptionId ?? 'reset'}
        open={openResetModel}
        onClose={() => setOpenResetModel(false)}
        subscription={selectedSubscription}
        handleResetSubscription={handleResetMachineLearningModelConfirmation}
      />

      {selectedSubscription && (
        <AlgorithmInputAdvancedDialog
          key={selectedSubscription?.JobSubscriptionId ?? 'advanced'}
          open={algorithmInputOpen}
          onClose={() => setAlgorithmInputOpen(false)}
          subscription={selectedSubscription}
          update={handleUpdateSubscription}
          specificationData={algorithmInputSpec}
        />
      )}

      {selectedSubscription && (
        <AlgorithmInputSimpleDialog
          key={selectedSubscription?.JobSubscriptionId ?? 'simple'}
          open={algorithmInputFormOpen}
          onClose={() => setAlgorithmInputFormOpen(false)}
          subscription={selectedSubscription}
          algorithmInput={selectedSubscription?.AlgorithmInput || {}}
          update={handleUpdateSubscription}
          specificationData={algorithmInputFormSpec}
          updateMachineLearningModel={updateMachineLearningModel}
        />
      )}

      <OccupancyPredictionForm
        open={modelAnalysisDialogOpen}
        onClose={() => {
          setModelAnalysisDialogOpen(false);
          setGeneratedOccupancyPlan(null);
        }}
        subscription={selectedSubscription}
        actuatorPlan={publishedActuatorPlan}
        initialPrediction={occupancyPrediction}
        occupancySourceExclusions={occupancySourceExclusions}
        generateOccupancyPredictionAnalysis={
          generateOccupancyPredictionAnalysis
        }
        setGeneratedOccupancyPlan={() => setGeneratedOccupancyPlan(null)}
        generatedOccupancyPlan={generatedOccupancyPlan}
        isGenerating={isGeneratingOccupancyAnalysis}
      />

      <Outlet />
    </Container>
  );
}
